import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { progressAPI, bookmarksAPI, sessionsAPI } from '../../services/api';
import part1Questions from '../../data/questions/part1.json';
import './QuizSetup.css';

const questionData = {
  '1': part1Questions
};

const partsInfo = [
  { number: 1, title: 'Electrocardiography' }
];

function QuizSetup() {
  const navigate = useNavigate();

  const [mode, setMode] = useState('tutor');
  const [questionCount, setQuestionCount] = useState(20);
  const [selectedParts, setSelectedParts] = useState({ 1: true });

  const [difficulties, setDifficulties] = useState({
    easy: true,
    medium: true,
    hard: true
  });

  const [unusedOnly, setUnusedOnly] = useState(false);
  const [incorrectOnly, setIncorrectOnly] = useState(false);
  const [bookmarkedOnly, setBookmarkedOnly] = useState(false);

  const [timerEnabled, setTimerEnabled] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(60);

  const [answeredQuestions, setAnsweredQuestions] = useState({});
  const [incorrectQuestions, setIncorrectQuestions] = useState(new Set());
  const [bookmarkedQuestions, setBookmarkedQuestions] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const [progressData, bookmarksData] = await Promise.all([
          progressAPI.getAll(),
          bookmarksAPI.getAll()
        ]);

        const allAnswers = {};
        const incorrect = new Set();

        for (const part of partsInfo) {
          try {
            const partData = await progressAPI.getPart(part.number);
            if (partData.answers) {
              Object.entries(partData.answers).forEach(([qId, answer]) => {
                allAnswers[qId] = answer;
                if (!answer.correct) {
                  incorrect.add(qId);
                }
              });
            }
          } catch (e) {
            // Part not started yet
          }
        }

        setAnsweredQuestions(allAnswers);
        setIncorrectQuestions(incorrect);

        if (bookmarksData.bookmarks) {
          setBookmarkedQuestions(new Set(bookmarksData.bookmarks.map(b => b.question_id)));
        }
      } catch (err) {
        console.error('Failed to load user data:', err);
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, []);

  const allQuestions = useMemo(() => {
    const questions = [];
    Object.entries(questionData).forEach(([partNum, data]) => {
      if (selectedParts[partNum]) {
        data.questions.forEach(q => {
          questions.push({ ...q, partNumber: parseInt(partNum, 10) });
        });
      }
    });
    return questions;
  }, [selectedParts]);

  const filteredQuestions = useMemo(() => {
    return allQuestions.filter(q => {
      const difficulty = q.difficulty || 'medium';
      if (!difficulties[difficulty]) return false;
      if (unusedOnly && answeredQuestions[q.id]) return false;
      if (incorrectOnly && !incorrectQuestions.has(q.id)) return false;
      if (bookmarkedOnly && !bookmarkedQuestions.has(q.id)) return false;
      return true;
    });
  }, [allQuestions, difficulties, unusedOnly, incorrectOnly, bookmarkedOnly, answeredQuestions, incorrectQuestions, bookmarkedQuestions]);

  const availableCount = filteredQuestions.length;
  const effectiveQuestionCount = Math.min(questionCount, availableCount);

  const toggleDifficulty = (diff) => {
    setDifficulties(prev => ({ ...prev, [diff]: !prev[diff] }));
  };

  const handleStartQuiz = async () => {
    if (effectiveQuestionCount === 0) return;

    const shuffled = [...filteredQuestions].sort(() => Math.random() - 0.5);
    const selected = shuffled.slice(0, effectiveQuestionCount);
    const questionIds = selected.map(q => q.id);

    try {
      const result = await sessionsAPI.create(
        mode,
        questionIds,
        timerEnabled ? timerSeconds : null
      );

      navigate(`/quiz/${result.sessionId}`);
    } catch (err) {
      console.error('Failed to create quiz session:', err);
      alert('Failed to start quiz. Please try again.');
    }
  };

  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="quiz-setup">
      <div className="quiz-setup-header">
        <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
        <h1>Create Quiz</h1>
      </div>

      <div className="quiz-setup-card">
        <section className="setup-section">
          <h3>Mode</h3>
          <div className="mode-options">
            <label className={`mode-option ${mode === 'tutor' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="mode"
                value="tutor"
                checked={mode === 'tutor'}
                onChange={() => setMode('tutor')}
              />
              <div className="mode-content">
                <strong>Tutor Mode</strong>
                <span>See feedback after each question</span>
              </div>
            </label>
            <label className={`mode-option ${mode === 'test' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="mode"
                value="test"
                checked={mode === 'test'}
                onChange={() => setMode('test')}
              />
              <div className="mode-content">
                <strong>Test Mode</strong>
                <span>See all results at the end</span>
              </div>
            </label>
          </div>
        </section>

        <section className="setup-section">
          <h3>Number of Questions</h3>
          <div className="question-count-slider">
            <input
              type="range"
              min="5"
              max={Math.max(availableCount, 5)}
              value={Math.min(questionCount, availableCount)}
              onChange={(e) => setQuestionCount(parseInt(e.target.value, 10))}
            />
            <span className="count-display">{effectiveQuestionCount}</span>
          </div>
          <div className="count-presets">
            {[5, 10, 20, 40].map(n => (
              <button
                key={n}
                className={`preset-btn ${questionCount === n ? 'active' : ''}`}
                onClick={() => setQuestionCount(n)}
                disabled={availableCount < n}
              >
                {n}
              </button>
            ))}
            <button
              className={`preset-btn ${questionCount >= availableCount ? 'active' : ''}`}
              onClick={() => setQuestionCount(availableCount)}
            >
              All ({availableCount})
            </button>
          </div>
        </section>

        <section className="setup-section">
          <h3>Difficulty</h3>
          <div className="difficulty-options">
            {['easy', 'medium', 'hard'].map(diff => (
              <label key={diff} className={`difficulty-checkbox ${diff}`}>
                <input
                  type="checkbox"
                  checked={difficulties[diff]}
                  onChange={() => toggleDifficulty(diff)}
                />
                <span>{diff.charAt(0).toUpperCase() + diff.slice(1)}</span>
              </label>
            ))}
          </div>
        </section>

        <section className="setup-section">
          <h3>Special Filters</h3>
          <div className="special-filters">
            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={unusedOnly}
                onChange={() => setUnusedOnly(!unusedOnly)}
              />
              <span>Unused questions only</span>
              <small>({allQuestions.filter(q => !answeredQuestions[q.id]).length} available)</small>
            </label>
            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={incorrectOnly}
                onChange={() => setIncorrectOnly(!incorrectOnly)}
              />
              <span>Previously incorrect only</span>
              <small>({incorrectQuestions.size} available)</small>
            </label>
            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={bookmarkedOnly}
                onChange={() => setBookmarkedOnly(!bookmarkedOnly)}
              />
              <span>Bookmarked only</span>
              <small>({bookmarkedQuestions.size} available)</small>
            </label>
          </div>
        </section>

        <section className="setup-section">
          <h3>Timer</h3>
          <div className="timer-settings">
            <label className="timer-toggle">
              <input
                type="checkbox"
                checked={timerEnabled}
                onChange={() => setTimerEnabled(!timerEnabled)}
              />
              <span>Enable timer</span>
            </label>
            {timerEnabled && (
              <div className="timer-input">
                <input
                  type="number"
                  min="10"
                  max="300"
                  value={timerSeconds}
                  onChange={(e) => setTimerSeconds(Math.max(10, Math.min(300, parseInt(e.target.value, 10) || 60)))}
                />
                <span>seconds per question</span>
              </div>
            )}
          </div>
        </section>

        <div className="quiz-summary">
          <p>
            <strong>{effectiveQuestionCount}</strong> questions available with current filters
          </p>
          {effectiveQuestionCount === 0 && (
            <p className="no-questions-warning">
              No questions match your filters. Try adjusting your selection.
            </p>
          )}
        </div>

        <button
          className="btn btn-primary start-quiz-btn"
          onClick={handleStartQuiz}
          disabled={effectiveQuestionCount === 0}
        >
          Start Quiz
        </button>
      </div>
    </div>
  );
}

export default QuizSetup;
